import React, { useState, useEffect, useCallback, useRef } from 'react';
import { friendlyDeleteError } from '../utils/deleteError';
import { useExport } from './header/exporters';
import { useMoney } from '../hooks/useMoney';
import { getAppLocale } from '../utils/locale';
import idColumn from './ui/idColumn';
import { useNavigate } from 'react-router-dom';
import DataTable, { actionsColumn } from './DataTable';
import ContentCard from './ContentCard';
import TagMultiSelect from './ui/TagMultiSelect';
import SearchableSelect from './ui/SearchableSelect';
import RichTextEditor from './ui/RichTextEditor';
import { QuoteFormFields } from './QuoteFormFields';
import { CurrencyInput } from './ui/CurrencyInput';
import { MetricCard } from './MetricCards';
import EmptyState from './EmptyState';
import Icon from './Icon';
import LineItemsEditor, { LineItem, LineItemFieldConfig } from './ui/LineItemsEditor';
import AttachmentsPanel from './ui/AttachmentsPanel';
import RecordActivity from './ui/RecordActivity';
import { ParsedVendor } from '../data/vendorData';
import { BudgetCategory } from '../types/ui/Budget';
import { VendorQuote, VendorQuoteItem } from '../types/ui/VendorQuote';
import { loadBudget, refreshBudgetFromApi, BUDGET_CHANGE_EVENT } from '../utils/budgetStorage';
import { useStoredEvents } from '../hooks/useStoredEvents';
import {
  loadQuotes, createQuote, updateQuote, acceptQuote, rejectQuote, deleteQuote,
  loadQuoteItems, addQuoteItem, updateQuoteItem, deleteQuoteItem,
} from '../services/VendorQuoteService';
import { useNotifications } from './NotificationProvider';
import { useConfirm } from './ui/ConfirmProvider';
import { formatDate } from '../utils/formatDate';
import { fmtId } from '../utils/jiraId';
import { compareQuotesByCreation } from '../utils/quoteOrder';
import { isOutOfTown, vendorLocalityReason, shouldWarnTravelCost } from '../utils/vendorLocation';
import { getAttachments, resolveMediaUrl, type Attachment } from '../services/uploadService';
import '../styles/components/QuotesTracker.css';
import '../styles/components/DataTable.css';
// Reuses the .vfp-* line-item classes VendorFormPage already established
// for vendor_booking_items — same shape of editor, different parent entity.
import '../styles/components/VendorFormPage.css';
// .poe-add-btn — the dashed-border "+ Add Item" trigger, shared with
// ProcurementOrderEdit's identical copy of the same rule.
import '../styles/components/FormPage.css';
import { usePublishRecordList } from '../hooks/useRecordList';

interface QuoteDraft {
  categoryId: string;
  eventIds: string[];
  dateReceived: string;
  notes: string;
  /** Directory vendor this quote belongs to. Editable only while the quote is
   *  still unlinked — accepting requires it, and a quote captured from a
   *  typed-in name has no link until someone supplies one. */
  vendorId: string;
  /** NGN amount to draw against the Contingency budget line once this quote
   *  is accepted — a string while editing, same convention as other numeric
   *  form fields in this component. */
  contingencyAmount: string;
}

interface QuotesTrackerProps {
  vendors?: ParsedVendor[];
  // Parsed from the /quotes/QT-XXX URL (App.tsx) — the plain numeric id
  // matching VendorQuote.id. Undefined on the list view.
  quoteId?: string;
  /** True on /quotes/new. The add form used to be local state with no URL of
   *  its own, unlike every other entity's /thing/new route. */
  newQuote?: boolean;
}

// A blank row for LineItemsEditor, keyed the same way the editor keys its own
// added rows. Lives here because the "+ Add Item" button now sits in the
// section header rather than inside the editor.
const blankLineItem = (fields: LineItemFieldConfig[]): LineItem => {
  const values: Record<string, string> = {};
  for (const f of fields) values[f.key] = '';
  return { id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, values };
};

const QuotesTracker: React.FC<QuotesTrackerProps> = ({ vendors = [], quoteId, newQuote = false }) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();
  const confirm = useConfirm();
  const money = useMoney();
  const [quotes, setQuotes] = useState<VendorQuote[]>([]);
  // Reports the current (sorted) id list up to the breadcrumb header, same
  // event-based pattern as quote:detail-status — lets the Prev/Next buttons
  // there walk the list currently loaded here without lifting `quotes`
  // itself into App state.
  usePublishRecordList('quote', [...quotes].sort(compareQuotesByCreation).map(q => q.id));
  const [categories, setCategories] = useState<BudgetCategory[]>([]);
  const events = useStoredEvents();
  const [loading, setLoading] = useState(true);
  const showForm = newQuote;
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  useEffect(() => {
    const onSet = (e: Event) => setViewMode((e as CustomEvent).detail);
    window.addEventListener('quote:view-mode', onSet);
    return () => window.removeEventListener('quote:view-mode', onSet);
  }, []);

  const [vendorSearch, setVendorSearch] = useState('');
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [eventIds, setEventIds] = useState<string[]>([]);
  const [dateReceived, setDateReceived] = useState('');
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState('');
  // Line items entered on the New Quote form, before the quote itself has an
  // id to attach them to — held locally and POSTed right after the quote is
  // created, matching ProcurementOrderEdit's create flow.
  const [newQuoteItems, setNewQuoteItems] = useState<LineItem[]>([]);
  const [rateCardAtts, setRateCardAtts] = useState<Attachment[]>([]);
  // Files attached to the quote itself (invoices, signed pro-formas), as
  // opposed to the vendor's rate card above. These rows already existed —
  // they were just never surfaced anywhere in the UI.

  useEffect(() => {
    if (!selectedVendorId) { setRateCardAtts([]); return; }
    getAttachments('vendor', selectedVendorId)
      .then(atts => setRateCardAtts(atts.filter(a => a.is_rate_card)))
      .catch(() => setRateCardAtts([]));
  }, [selectedVendorId]);

  // ── Line items + notes (for the quote opened via the /quotes/QT-XXX URL) ──
  const [quoteItems, setQuoteItems] = useState<VendorQuoteItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  // New (not-yet-persisted) rows added via LineItemsEditor's own "+ Add
  // item" button, keyed by its client-generated id. Held here instead of
  // created immediately — matches itemDraft's save-on-button behavior for
  // existing rows, so a new row doesn't hit the server until Save, and
  // there's no half-typed row left behind by a fast-typing race.
  const [newItemDrafts, setNewItemDrafts] = useState<Record<string, {
    description: string; quantity: number; unitCost: number;
    categoryId: string | null; isBundleAdjustment: boolean;
  }>>({});

  // ── Draft state for the Vendor/Category/Events/Date/Notes fields on the
  // detail view. Nothing here commits on change/blur any more — the whole
  // app's other detail/edit pages (e.g. the
  // forms/*FormPage set) use an explicit Save button instead of autosave,
  // so this quote-detail view now matches that convention. Line items keep
  // their own separate per-row save (LineItemsEditor) — untouched.
  const [draft, setDraft] = useState<QuoteDraft>({ categoryId: '', eventIds: [], dateReceived: '', notes: '', vendorId: '', contingencyAmount: '' });

  // Line-item edits are held here until Save is pressed — the server copy
  // stays untouched meanwhile, so this is also what tells us what changed.
  const [itemDraft, setItemDraft] = useState<Record<string, VendorQuoteItem>>({});
  useEffect(() => { setItemDraft({}); }, [quoteId]);


  const loadAll = useCallback(async () => {
    setLoading(true);
    // Events are read from the local cache: the /events API is disabled.
    const [q, cats] = await Promise.all([loadQuotes(), refreshBudgetFromApi()]);
    setQuotes(q);
    setCategories(cats);
    setLoading(false);
  }, []);

  useEffect(() => {
    void loadAll();
    const handler = () => void refreshBudgetFromApi().then(setCategories);
    window.addEventListener(BUDGET_CHANGE_EVENT, handler);
    return () => window.removeEventListener(BUDGET_CHANGE_EVENT, handler);
  }, [loadAll]);

  // "+ New Quote" lives in the breadcrumb bar (Header.tsx), not the page
  // body — matches every other list page's add-button placement.
  useEffect(() => {
    const onNew = () => navigate('/quotes/new');
    window.addEventListener('quote:new', onNew);
    return () => window.removeEventListener('quote:new', onNew);
  }, []);

  // Reports the add-form's open/closed state up to Header so its breadcrumb
  // ("Dashboard / Quotes / New Quote") and Save/Cancel actions can reflect
  // it — this page never changes URL when the form opens, so Header has no
  // other way to know.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('quote:new-status', { detail: showForm }));
  }, [showForm]);

  useEffect(() => {
    const onSave = () => void submitNewQuote();
    const onCancel = () => { resetForm(); navigate('/quotes'); };
    window.addEventListener('quote:save', onSave);
    window.addEventListener('quote:cancel-new', onCancel);
    return () => {
      window.removeEventListener('quote:save', onSave);
      window.removeEventListener('quote:cancel-new', onCancel);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorSearch, categoryId, selectedVendorId, eventIds, dateReceived, notes]);

  // Export button (Header): every quote on the list.
  useExport('quotes', {
    rows: quotes,
    ctx: {
      categoryName: id => categories.find(c => c.id === id)?.name ?? '',
      eventNames: ids => ids.map(id => events.find(e => e.id === id)?.title).filter(Boolean).join('; '),
    },
  });

  // Status/Category filters, breadcrumb-bar-driven like everything else here.
  // Categories are reported up to Header (which has no independent access to
  // budget category data) so it can populate the dropdown options.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('quote-categories-ready', {
      detail: categories.map(c => ({ id: c.id, name: c.name })),
    }));
  }, [categories]);

  useEffect(() => {
    const onStatus = (e: Event) => setStatusFilter((e as CustomEvent<string>).detail);
    const onCategory = (e: Event) => setCategoryFilter((e as CustomEvent<string>).detail);
    window.addEventListener('quote:status-filter-change', onStatus);
    window.addEventListener('quote:category-filter-change', onCategory);
    return () => {
      window.removeEventListener('quote:status-filter-change', onStatus);
      window.removeEventListener('quote:category-filter-change', onCategory);
    };
  }, []);

  const filteredQuotes = quotes.filter(q =>
    (statusFilter === 'all' || q.status === statusFilter) &&
    (!categoryFilter || q.categoryId === categoryFilter)
  );

  const resetForm = () => {
    setVendorSearch(''); setSelectedVendorId(''); setCategoryId('');
    setEventIds([]); setDateReceived(''); setNotes(''); setFormError('');
    setNewQuoteItems([]);
  };

  const submitNewQuote = async () => {
    if (!vendorSearch.trim()) { setFormError('Vendor name is required.'); return; }
    if (!categoryId) { setFormError('Pick a budget category.'); return; }
    setFormError('');
    try {
      const created = await createQuote({
        categoryId,
        vendorId: selectedVendorId || undefined,
        vendorName: vendorSearch.trim(),
        eventIds,
        dateReceived: dateReceived || undefined,
        notes: notes.trim() || undefined,
      });
      // The quote only gets an id once created — items typed in beforehand
      // were held locally, so create each one now that there's somewhere
      // for it to attach to.
      for (const item of newQuoteItems) {
        if (!item.values.description.trim()) continue;
        const isBundleAdjustment = item.values.isBundleAdjustment === 'true';
        await addQuoteItem(created.id, item.values.description, Number(item.values.quantity) || 1, Number(item.values.unitCost) || 0, {
          categoryId: isBundleAdjustment ? null : (item.values.categoryId || null),
          isBundleAdjustment,
        });
      }
      const withTotal = newQuoteItems.length > 0
        ? { ...created, totalAmount: newQuoteItems.reduce((sum, it) => sum + (Number(it.values.quantity) || 0) * (Number(it.values.unitCost) || 0), 0) }
        : created;
      setQuotes(prev => [withTotal, ...prev]);
      showSuccess(`Quote from "${created.vendorName}" added.`);
      resetForm();
      navigate('/quotes');
    } catch {
      showError('Failed to add quote.');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submitNewQuote();
  };

  const handleAccept = useCallback(async (quote: VendorQuote) => {
    try {
      const { quote: updated } = await acceptQuote(quote.id);
      setQuotes(prev => prev.map(q => q.id === updated.id ? updated : q));
      showSuccess(`Accepted — "${updated.vendorName}" now has a budget line ready for the real numbers.`);
      void refreshBudgetFromApi().then(setCategories);
    } catch (err) {
      // The server's refusals are specific and actionable ("no linked vendor",
      // "already accepted") — a blanket string threw all of that away and left
      // the failure unexplainable from the UI.
      showError(err instanceof Error ? err.message : 'Failed to accept quote.');
    }
  }, [showSuccess, showError]);

  const handleReject = useCallback(async (quote: VendorQuote) => {
    try {
      const updated = await rejectQuote(quote.id);
      setQuotes(prev => prev.map(q => q.id === updated.id ? updated : q));
      showSuccess(`Rejected — "${updated.vendorName}"'s quote is recorded as declined.`);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to reject quote.');
    }
  }, [showSuccess, showError]);

  // Opening a quote now navigates to /quotes/QT-XXX (App.tsx parses the id
  // back out and passes it down as the quoteId prop) instead of local
  // component state — gives the quote its own URL and breadcrumb segment.
  const openQuoteEdit = (quote: VendorQuote) => {
    navigate(`/quotes/${fmtId('QT', quote.id)}`);
  };

  const categoryName = (id: string) => categories.find(c => String(c.id) === id)?.name ?? '—';

  const expandedQuote = quoteId ? quotes.find(q => q.id === quoteId) ?? null : null;
  const quoteLocked = expandedQuote ? expandedQuote.status !== 'pending' : false;

  const pendingCount = quotes.filter(q => q.status === 'pending').length;
  const acceptedCount = quotes.filter(q => q.status === 'accepted').length;

  // Load line items whenever the URL's quote id changes.
  useEffect(() => {
    if (!quoteId) { setQuoteItems([]); return; }
    setNewItemDrafts({});
    setItemsLoading(true);
    loadQuoteItems(quoteId)
      .then(setQuoteItems)
      .catch((err: unknown) => showError(
        `Failed to load line items${err instanceof Error && err.message ? `: ${err.message}` : '.'}`
      ))
      .finally(() => setItemsLoading(false));
  }, [quoteId]);

  // Keep the draft in sync with the loaded quote — not on every keystroke,
  // only when we land on a (possibly different) quote, so an in-progress
  // edit is never clobbered by an unrelated quotes-list refresh.
  useEffect(() => {
    setDraft({
      categoryId: expandedQuote?.categoryId ?? '',
      eventIds: expandedQuote?.eventIds ?? [],
      dateReceived: expandedQuote?.dateReceived ?? '',
      notes: expandedQuote?.notes ?? '',
      vendorId: expandedQuote?.vendorId ?? '',
      contingencyAmount: expandedQuote?.contingencyAmount ? String(expandedQuote.contingencyAmount) : '',
    });
  }, [expandedQuote?.id]);

  // The directory vendor this quote's booking hangs off, matched on the slug
  // the quote stores. Falls back to a direct id match for any quote written
  // with the numeric id instead.
  const bookingVendor = expandedQuote?.vendorId
    ? vendors.find(v => v.apiKey === expandedQuote.vendorId)
      ?? vendors.find(v => v.id === expandedQuote.vendorId)
    : undefined;

  // ── Out-of-town cost signal ───────────────────────────────────────────────
  // A service vendor based outside the project city bills travel and
  // accommodation on top of their quote, so the figure on this page
  // understates the true cost — both against rival quotes and against the
  // category's planned_amount ceiling. Deliberately scoped to service
  // categories only: goods suppliers already carry their freight as
  // landing_cost_cents on the procurement order, so warning about "travel"
  // there would double-count it. See src/utils/vendorLocation.ts.
  const projectCity = events.find(e => e.city?.trim())?.city ?? '';
  const quoteVendorLocation = bookingVendor?.baseLocation;
  const quoteLocality = isOutOfTown(quoteVendorLocation, projectCity);
  // No project city on record means nothing to compare against — stay silent
  // rather than telling the planner the *vendor's* location is missing.
  const showTravelNotice = !!expandedQuote && !!projectCity.trim()
    && shouldWarnTravelCost(quoteLocality, categoryName(expandedQuote.categoryId));

  const isDraftDirty = !!expandedQuote && (
    draft.categoryId !== expandedQuote.categoryId
    || draft.dateReceived !== (expandedQuote.dateReceived ?? '')
    || draft.notes !== (expandedQuote.notes ?? '')
    || draft.eventIds.length !== expandedQuote.eventIds.length
    || draft.eventIds.some(id => !expandedQuote.eventIds.includes(id))
    || draft.vendorId !== (expandedQuote.vendorId ?? '')
    || Number(draft.contingencyAmount || 0) !== (expandedQuote.contingencyAmount ?? 0)
    || Object.keys(itemDraft).length > 0
    || Object.keys(newItemDrafts).length > 0
  );

  // Commits all pending draft field changes in a single PATCH, driven by
  // the header's Save button (Header.tsx, quote-detail case) — matches the
  // rest of the app's explicit-save convention instead of autosaving.
  const handleSaveQuoteEdit = useCallback(async () => {
    if (!expandedQuote || !isDraftDirty) return;
    // A quote past 'pending' is the frozen record of what was bid. The fields
    // are already disabled, but the breadcrumb Save button stayed enabled, so
    // pressing it fired a write that could only fail — which is what produced
    // the bare "Failed to save changes." on an accepted/rejected quote.
    if (quoteLocked) {
      showError(`This quote is ${expandedQuote.status} — it can no longer be edited.`);
      return;
    }

    const updates: Partial<Pick<VendorQuote, 'vendorId' | 'vendorName' | 'categoryId' | 'eventIds' | 'dateReceived' | 'notes' | 'contingencyAmount'>> = {};
    if (draft.categoryId !== expandedQuote.categoryId) updates.categoryId = draft.categoryId;
    if (draft.vendorId !== (expandedQuote.vendorId ?? '')) {
      updates.vendorId = draft.vendorId;
      // Keep the denormalised name in step with the link, so the list, search
      // index and accept-confirmation all read the directory's spelling rather
      // than whatever was originally typed in.
      const picked = vendors.find(v => v.id === draft.vendorId);
      if (picked) updates.vendorName = picked.name;
    }
    if (draft.eventIds.length !== expandedQuote.eventIds.length || draft.eventIds.some(id => !expandedQuote.eventIds.includes(id))) {
      updates.eventIds = draft.eventIds;
    }
    if (draft.dateReceived !== (expandedQuote.dateReceived ?? '')) updates.dateReceived = draft.dateReceived || undefined;
    if (draft.notes !== (expandedQuote.notes ?? '')) updates.notes = draft.notes.trim() || undefined;
    if (Number(draft.contingencyAmount || 0) !== (expandedQuote.contingencyAmount ?? 0)) {
      updates.contingencyAmount = Number(draft.contingencyAmount) || 0;
    }
    try {
      // Commit any pending line-item edits first, so one Save covers the whole
      // quote rather than leaving the rows behind.
      const savedItems: VendorQuoteItem[] = [];
      for (const [id, item] of Object.entries(itemDraft)) {
        savedItems.push(await updateQuoteItem(expandedQuote.id, id, {
          description: item.description,
          quantity: item.quantity,
          unitCost: item.unitCost,
          isBundleAdjustment: item.isBundleAdjustment,
          categoryId: item.categoryId,
        }));
      }
      if (savedItems.length > 0) {
        setQuoteItems(prev => prev.map(it => savedItems.find(sv => sv.id === it.id) ?? it));
        setItemDraft({});
      }

      // New rows only ever existed client-side until now — create each one,
      // skipping any left blank (a row added then never filled in).
      const createdItems: VendorQuoteItem[] = [];
      for (const draftItem of Object.values(newItemDrafts)) {
        if (!draftItem.description.trim()) continue;
        createdItems.push(await addQuoteItem(expandedQuote.id, draftItem.description, draftItem.quantity, draftItem.unitCost, {
          categoryId: draftItem.categoryId,
          isBundleAdjustment: draftItem.isBundleAdjustment,
        }));
      }
      if (createdItems.length > 0) {
        setQuoteItems(prev => [...prev, ...createdItems]);
      }
      setNewItemDrafts({});

      const updated = await updateQuote(expandedQuote.id, updates);
      setQuotes(prev => prev.map(q => q.id === updated.id ? updated : q));
      showSuccess('Quote saved.');
    } catch (err) {
      showError(err instanceof Error && err.message ? err.message : 'Failed to save changes.');
    }
  }, [expandedQuote, draft, isDraftDirty, vendors, itemDraft, newItemDrafts, showSuccess, showError]);

  useEffect(() => {
    const onSaveEdit = () => void handleSaveQuoteEdit();
    window.addEventListener('quote:save-edit', onSaveEdit);
    return () => window.removeEventListener('quote:save-edit', onSaveEdit);
  }, [handleSaveQuoteEdit]);

  const handleDeleteQuote = useCallback(async () => {
    if (!expandedQuote) return;
    if (!(await confirm({
      title: `Delete ${expandedQuote.vendorName}'s quote?`,
      description: "This can't be undone.",
      confirmLabel: 'Delete quote',
    }))) return;
    try {
      await deleteQuote(expandedQuote.id);
      setQuotes(prev => prev.filter(q => q.id !== expandedQuote.id));
      showSuccess('Quote deleted.');
      navigate('/quotes');
    } catch (err) {
      showError(friendlyDeleteError(err, { record: 'quote', action: 'delete' }));
    }
  }, [expandedQuote, showSuccess, showError, navigate, confirm]);

  useEffect(() => {
    const onDelete = () => void handleDeleteQuote();
    window.addEventListener('quote-detail:delete', onDelete);
    return () => window.removeEventListener('quote-detail:delete', onDelete);
  }, [handleDeleteQuote]);

  // Reports status + dirty state up to Header (quote-detail case) so its
  // breadcrumb status stepper and Save button can reflect them — this page
  // never changes URL on edits, so Header has no other way to know.
  useEffect(() => {
    if (!expandedQuote) return;
    window.dispatchEvent(new CustomEvent('quote:detail-status', { detail: expandedQuote.status }));
  }, [expandedQuote?.id, expandedQuote?.status]);

  useEffect(() => {
    if (!expandedQuote) return;
    window.dispatchEvent(new CustomEvent('quote:detail-dirty', { detail: isDraftDirty }));
  }, [expandedQuote?.id, isDraftDirty]);

  // Mirrors the backend's own recompute (sum of quantity*unitCost) so the
  // Total column stays in sync without a full quote-list reload.
  const applyItemsTotal = (qid: string, items: VendorQuoteItem[]) => {
    const total = items.reduce((sum, it) => sum + it.quantity * it.unitCost, 0);
    setQuotes(prev => prev.map(q => q.id === qid ? { ...q, totalAmount: total } : q));
  };

  const handleUpdateItem = useCallback(async (item: VendorQuoteItem, fields: Partial<Pick<VendorQuoteItem, 'description' | 'quantity' | 'unitCost' | 'categoryId' | 'isBundleAdjustment'>>) => {
    if (!quoteId) return;
    try {
      const updated = await updateQuoteItem(quoteId, item.id, fields);
      // Functional update: this now runs from a debounced timer, so the
      // captured quoteItems could be a render or two stale and would revert
      // a concurrent edit to another row.
      setQuoteItems(prev => {
        const next = prev.map(it => it.id === item.id ? updated : it);
        applyItemsTotal(quoteId, next);
        return next;
      });
    } catch (err: any) {
      showError(err?.message ?? 'Failed to update item — the quote may no longer be pending.');
    }
  }, [quoteId]);

  const handleDeleteItem = useCallback(async (item: VendorQuoteItem) => {
    if (!quoteId) return;
    try {
      await deleteQuoteItem(quoteId, item.id);
      const next = quoteItems.filter(it => it.id !== item.id);
      setQuoteItems(next);
      applyItemsTotal(quoteId, next);
    } catch (err: any) {
      showError(err?.message ?? 'Failed to remove item — the quote may no longer be pending.');
    }
  }, [quoteId, quoteItems]);

  // Bridges LineItemsEditor's "whole array" onChange to the existing
  // per-row update/delete calls above, plus new rows added via the editor's
  // own "+ Add item" button. A new row's id (LineItemsEditor's genId()) never
  // matches an existing quoteItems id, so that's how the two are told apart.
  const handleLineItemsChange = useCallback((nextItems: LineItem[]) => {
    const existingIds = new Set(quoteItems.map(it => String(it.id)));
    const nextIds = new Set(nextItems.map(it => it.id));

    const removedExisting = quoteItems.find(it => !nextIds.has(String(it.id)));
    // Removing an already-saved row is an explicit click, not typing, so it
    // still commits immediately — there is nothing to "finish typing" first.
    if (removedExisting) { void handleDeleteItem(removedExisting); return; }

    // Edits to saved rows stay local until Save. Nothing is written per
    // keystroke, which is what used to reset the field from the server
    // mid-word.
    setItemDraft(prev => {
      const next = { ...prev };
      for (const existing of quoteItems) {
        const item = nextItems.find(it => it.id === String(existing.id));
        if (!item) continue;
        const isBundleAdjustment = item.values.isBundleAdjustment === 'true';
        const fields = {
          description: item.values.description,
          quantity: Number(item.values.quantity) || 0,
          unitCost: Number(item.values.unitCost) || 0,
          isBundleAdjustment,
          categoryId: isBundleAdjustment ? null : (item.values.categoryId || null),
        };
        const changed = existing.description !== fields.description
          || existing.quantity !== fields.quantity
          || existing.unitCost !== fields.unitCost
          || !!existing.isBundleAdjustment !== fields.isBundleAdjustment
          || (existing.categoryId ?? null) !== fields.categoryId;
        if (changed) next[String(existing.id)] = { ...existing, ...fields };
        else delete next[String(existing.id)];
      }
      return next;
    });

    // New (not-yet-persisted) rows never hit the server here — a row removed
    // before Save just drops out of the draft below, no delete call needed.
    setNewItemDrafts(prev => {
      const next: typeof prev = {};
      for (const item of nextItems) {
        if (existingIds.has(item.id)) continue;
        const isBundleAdjustment = item.values.isBundleAdjustment === 'true';
        next[item.id] = {
          description: item.values.description,
          quantity: Number(item.values.quantity) || 0,
          unitCost: Number(item.values.unitCost) || 0,
          isBundleAdjustment,
          categoryId: isBundleAdjustment ? null : (item.values.categoryId || null),
        };
      }
      return next;
    });
  }, [quoteItems, handleDeleteItem]);

  // Rows render from the draft where one exists, so what you type stays on
  // screen until Save commits it. New rows (never persisted) are appended
  // from newItemDrafts after the saved ones.
  const lineItems: LineItem[] = [
    ...quoteItems.map(base => itemDraft[String(base.id)] ?? base).map(it => ({
    id: String(it.id),
    values: {
      description: it.description,
      quantity: String(it.quantity),
      unitCost: String(it.unitCost),
      isBundleAdjustment: it.isBundleAdjustment ? 'true' : 'false',
      categoryId: it.categoryId ?? '',
    },
    })),
    ...Object.entries(newItemDrafts).map(([id, it]) => ({
      id,
      values: {
        description: it.description,
        quantity: String(it.quantity),
        unitCost: String(it.unitCost),
        isBundleAdjustment: it.isBundleAdjustment ? 'true' : 'false',
        categoryId: it.categoryId ?? '',
      },
    })),
  ];

  const lineItemFields = (thisQuoteCategoryId: string): LineItemFieldConfig[] => [
    // `header` is set explicitly rather than left to fall back to
    // `placeholder`: a placeholder is a terse in-field hint ("Unit ₦"), which
    // reads wrong once it's promoted to a column heading. The currency input
    // already renders its own ₦ prefix, so the symbol is redundant here too.
    { key: 'description', type: 'text', header: 'Description', placeholder: 'Description', row: 1 },
    { key: 'quantity', type: 'number', header: 'Qty', placeholder: 'Qty', min: '0', row: 1 },
    { key: 'unitCost', type: 'currency', header: 'Unit Cost', placeholder: '0', min: '0', row: 1 },
    {
      key: 'isBundleAdjustment', type: 'checkbox', row: 1, compact: true, icon: 'link',
      label: 'Bundle-wide (prorated across categories)',
    },
    {
      // Sits on the metadata row, which is a plain flex line — without a width
      // the picker stretched the full card. It only ever holds a category name.
      key: 'categoryId', type: 'picker', row: 2, width: 280,
      showIf: values => values.isBundleAdjustment !== 'true',
      options: [
        // Means "no override" — the item inherits the quote's own category.
        // Leading with "Same as quote" keeps the intent readable when the
        // category name is long enough to truncate the trigger.
        { value: '', label: `Same as quote · ${categoryName(thisQuoteCategoryId)}` },
        ...categories.filter(c => String(c.id) !== thisQuoteCategoryId).map(c => ({ value: String(c.id), label: c.name })),
      ],
    },
  ];

  if (loading) return <EmptyState icon="document" title="Loading quotes…" compact />;

  return (
    <div className="wt-container fade-in">
      {!expandedQuote && !showForm && (
        <section className="stats-grid">
          <MetricCard label="Total Quotes" value={quotes.length} tone="blue" icon="document" />
          <MetricCard label="Pending" value={pendingCount} tone="orange" icon="activity" />
          <MetricCard label="Accepted" value={acceptedCount} tone="green" icon="check" />
        </section>
      )}

      {showForm && (
        <div className="detail-layout">
          <form className="inline-form-page" onSubmit={handleSubmit}>
            <QuoteFormFields
              vendors={vendors}
              categories={categories}
              events={events}
              values={{ vendorSearch, selectedVendorId, categoryId, eventIds, dateReceived, notes }}
              onChange={updates => {
                if (updates.vendorSearch !== undefined) setVendorSearch(updates.vendorSearch);
                if (updates.selectedVendorId !== undefined) setSelectedVendorId(updates.selectedVendorId);
                if (updates.categoryId !== undefined) setCategoryId(updates.categoryId);
                if (updates.eventIds !== undefined) setEventIds(updates.eventIds);
                if (updates.dateReceived !== undefined) setDateReceived(updates.dateReceived);
                if (updates.notes !== undefined) setNotes(updates.notes);
              }}
            />
            {formError && <span className="field-error">{formError}</span>}

            <div className="vfp-line-items form-section">
              <div className="qt-line-items-header">
                <p className="form-section-title">Line Items</p>
                <div className="qt-line-items-header-actions">
                  {newQuoteItems.length > 0 && (
                    <span className="poe-items-total">
                      {newQuoteItems.length} {newQuoteItems.length === 1 ? 'item' : 'items'}
                    </span>
                  )}
                  <button
                    type="button"
                    className="poe-add-btn"
                    onClick={() => setNewQuoteItems(prev => [...prev, blankLineItem(lineItemFields(categoryId))])}
                  >
                    + Add Item
                  </button>
                </div>
              </div>
              <LineItemsEditor
                items={newQuoteItems}
                fields={lineItemFields(categoryId)}
                onChange={setNewQuoteItems}
                showAddButton={false}
                total={{ compute: v => (Number(v.quantity) || 0) * (Number(v.unitCost) || 0) }}
                showFooterTotal={false}
              />
              {newQuoteItems.length > 0 && (
                <div className="lie-summary">
                  <div className="lie-summary-field">
                    <span className="lie-summary-label">Total</span>
                    <div className="lie-summary-box">
                      <span className="lie-summary-currency">{money.symbol}</span>
                      <span className="lie-summary-amount">
                        {newQuoteItems
                          .reduce((sum, it) => sum + (Number(it.values.quantity) || 0) * (Number(it.values.unitCost) || 0), 0)
                          .toLocaleString(getAppLocale())}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Submit is driven from the header (Header.tsx's quotes/quoteNewOpen
                case dispatches quote:save) — this hidden button just keeps
                Enter-to-submit working inside the form. */}
            <button type="submit" className="u-hidden" aria-hidden="true" />
          </form>

          <aside className="detail-sidebar">
            <section className="side-section">
              <h4>Recording a Quote</h4>
              <p className="form-hint">
                Track vendor bids here before they become a real budget line. Accepting a quote later
                creates the matching budget entry automatically.
              </p>
            </section>
            {selectedVendorId && (() => {
              const selectedVendor = vendors.find(v => v.id === selectedVendorId);
              const hasRateCard = !!selectedVendor?.rateCardNotes || !!selectedVendor?.rateCardValidUntil || rateCardAtts.length > 0;
              if (!hasRateCard) return null;
              const isStale = selectedVendor?.rateCardValidUntil ? new Date(selectedVendor.rateCardValidUntil) < new Date() : false;
              return (
                <section className="side-section">
                  <h4>Rate Card — {vendorSearch}</h4>
                  {selectedVendor?.rateCardValidUntil && (
                    <p className={isStale ? 'field-error' : 'form-hint'}>
                      {isStale ? 'Expired' : 'Valid until'} {formatDate(selectedVendor.rateCardValidUntil)}
                    </p>
                  )}
                  {rateCardAtts.map(att => (
                    <a key={att.id} href={resolveMediaUrl(att.url)} target="_blank" rel="noopener noreferrer" className="vfp-contract-link qt-rate-card-link">
                      <Icon name="document" size={14} /> {att.label ?? att.original_name ?? 'Rate card file'} ↗
                    </a>
                  ))}
                  {selectedVendor?.rateCardNotes && (
                    <p className="form-hint qt-rate-card-notes">{selectedVendor.rateCardNotes}</p>
                  )}
                </section>
              );
            })()}
            {selectedVendorId && (() => {
              const priorQuotes = quotes.filter(q => q.vendorId === selectedVendorId);
              if (priorQuotes.length === 0) return null;
              return (
                <section className="side-section">
                  <h4>Other Quotes from {vendorSearch}</h4>
                  {priorQuotes.map(q => (
                    <div key={q.id} className="bvfp-quote-row">
                      <span>{categoryName(q.categoryId)}</span>
                      <strong>{q.totalAmount > 0 ? money.format(q.totalAmount) : '—'}</strong>
                    </div>
                  ))}
                </section>
              );
            })()}
          </aside>
        </div>
      )}

      {!showForm && !expandedQuote && viewMode === 'grid' && (
        filteredQuotes.length === 0 ? (
          <EmptyState icon="document" title="No quotes yet" description="Track vendor bids here before any of them becomes a real budget line." />
        ) : (
          <div className="quotes-grid">
            {filteredQuotes.map(q => (
              <ContentCard
                key={q.id}
                hideImage
                title={q.vendorName}
                subtitle={categoryName(q.categoryId)}
                status={{
                  label: q.status.charAt(0).toUpperCase() + q.status.slice(1),
                  type: q.status === 'accepted' ? 'active' : q.status === 'rejected' ? 'inactive' : 'pending',
                }}
                details={[
                  {
                    label: 'Events',
                    value: q.eventIds.length
                      ? q.eventIds.map(eid => events.find(e => e.id === eid)?.title ?? eid).join(', ')
                      : 'All events',
                  },
                  { label: 'Date Received', value: formatDate(q.dateReceived) },
                ]}
                pricing={{ amount: q.totalAmount, currency: money.symbol }}
                description={q.notes || undefined}
                descriptionLines={4}
                onClick={() => openQuoteEdit(q)}
              />
            ))}
          </div>
        )
      )}

      {!showForm && !expandedQuote && viewMode === 'table' && <DataTable
            pagination={{ enabled: true, pageSize: 25, showPageSizeSelector: true, pageSizeOptions: [25, 50, 100] }}
        data={filteredQuotes}
        emptyMessage="No quotes yet — track vendor bids here before any of them becomes a real budget line."
        rowClassName={q => q.status !== 'pending' ? 'dt-row-rejected' : ''}
        onRowClick={openQuoteEdit}
        columns={[
          idColumn('quote'),
          // Explicit widths — under the table's table-layout: auto, a column
          // with no width is sized from whatever content happens to be
          // visible, so sorting (which reorders which rows are on-screen)
          // shifted every column's width on every click. Notes is left
          // unset deliberately: it's the most length-variable field, so it
          // absorbs whatever space the pinned columns don't need.
          { key: 'vendor', header: 'Vendor', width: '180px', render: (_v, q) => <strong>{q.vendorName}</strong> },
          { key: 'category', header: 'Category', width: '130px', render: (_v, q) => categoryName(q.categoryId) },
          {
            key: 'events',
            header: 'Events',
            width: '220px',
            render: (_v, q) => q.eventIds.length
              ? q.eventIds.map(eid => events.find(e => e.id === eid)?.title ?? eid).join(', ')
              : 'All events',
          },
          {
            key: 'status',
            header: 'Status',
            width: '110px',
            render: (_v, q) => (
              <span className={`ds-badge ${q.status === 'accepted' ? 'ds-badge-success' : q.status === 'rejected' ? 'ds-badge-danger' : 'ds-badge-neutral'}`}>
                {q.status.charAt(0).toUpperCase() + q.status.slice(1)}
              </span>
            ),
          },
          {
            key: 'total', header: 'Total', width: '130px', align: 'right',
            render: (_v, q) => q.totalAmount > 0 ? money.format(q.totalAmount) : '—',
          },
          { key: 'dateReceived', header: 'Date Received', width: '130px', render: (_v, q) => formatDate(q.dateReceived) },
          { key: 'notes', header: 'Notes', className: 'qt-col-notes', clamp: 4, render: (_v, q) => q.notes || '—' },
          actionsColumn<VendorQuote>([
            {
              key: 'accept', icon: 'check', title: 'Accept', variant: 'success',
              show: q => q.status === 'pending',
              onClick: handleAccept,
            },
            {
              key: 'reject', icon: 'x', title: 'Reject', variant: 'danger',
              show: q => q.status === 'pending',
              onClick: handleReject,
            },
          ]),
        ]}
      />}

      {!showForm && expandedQuote && (
        <>
          <div className="detail-layout">
          <div className="detail-main">
          <div className="inline-form-page">
            {/* Same field set/layout as the New Quote form (below), filled in
                with this quote's current values instead of empty — Vendor
                stays read-only (changing it isn't supported by the
                accept/reconcile flow). Category/Events/Date Received/Notes
                all edit local draft state now — nothing commits until the
                header's Save button fires quote:save-edit (see
                handleSaveQuoteEdit above), matching the rest of the app's
                explicit-save convention instead of autosaving. */}
            <div className="form-row">
              <div className="form-group">
                <label>Vendor</label>
                {/* Already linked, or frozen past 'pending' — show the name.
                    Unlinked and still pending, the quote can't be accepted at
                    all (the server refuses without a vendor), so this is the
                    one place to repair it. */}
                {expandedQuote.vendorId || quoteLocked ? (
                  <input type="text" value={expandedQuote.vendorName} disabled />
                ) : (
                  <>
                    <SearchableSelect
                      value={draft.vendorId}
                      onChange={id => setDraft(d => ({ ...d, vendorId: id || '' }))}
                      options={vendors.map(v => ({ value: v.id, label: v.name }))}
                      placeholder={`${expandedQuote.vendorName} — link to a directory vendor…`}
                    />
                    <span className="form-label-hint">
                      Recorded as “{expandedQuote.vendorName}”. Link it to a vendor before accepting.
                    </span>
                  </>
                )}
              </div>
              <div className="form-group">
                <label>Budget Category</label>
                <SearchableSelect
                  value={draft.categoryId}
                  onChange={id => { if (!quoteLocked && id) setDraft(d => ({ ...d, categoryId: id })); }}
                  options={categories.map(c => ({ value: c.id, label: c.name }))}
                  placeholder="Select a category…"
                  // Was guarded only in onChange, so on a locked quote the
                  // dropdown still opened and silently discarded the pick —
                  // every other field here disables properly.
                  disabled={quoteLocked}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Events <span className="form-label-hint">(optional)</span></label>
                <TagMultiSelect
                  values={draft.eventIds.map(eid => events.find(e => e.id === eid)?.title ?? eid)}
                  options={events.map(ev => ev.title)}
                  onChange={titles => {
                    if (quoteLocked) return;
                    const ids = titles.map(t => events.find(ev => ev.title === t)?.id ?? t);
                    setDraft(d => ({ ...d, eventIds: ids }));
                  }}
                  placeholder="All events"
                  allowCreate={false}
                  disabled={quoteLocked}
                />
              </div>
              <div className="form-group">
                <label>Date Received</label>
                <input
                  type="date"
                  value={draft.dateReceived}
                  disabled={quoteLocked}
                  onChange={e => setDraft(d => ({ ...d, dateReceived: e.target.value }))}
                />
              </div>
            </div>
            <RichTextEditor
              label="Notes"
              value={draft.notes}
              onChange={val => setDraft(d => ({ ...d, notes: val }))}
              placeholder="Anything worth remembering about this bid… (optional)"
              rows={2}
              disabled={quoteLocked}
            />

            <div className="vfp-line-items form-section">
              <div className="qt-line-items-header">
                {/* Vendor name dropped: it's already the page heading directly above. */}
                <p className="form-section-title">Line Items</p>
                <div className="qt-line-items-header-actions">
                  {lineItems.length > 0 && (
                    <span className="poe-items-total">
                      {lineItems.length} {lineItems.length === 1 ? 'item' : 'items'}
                    </span>
                  )}
                  {!quoteLocked && (
                    <button
                      type="button"
                      className="poe-add-btn"
                      onClick={() => {
                        const row = blankLineItem(lineItemFields(expandedQuote.categoryId));
                        setNewItemDrafts(prev => ({
                          ...prev,
                          [row.id]: {
                            description: '', quantity: 1, unitCost: 0,
                            categoryId: null, isBundleAdjustment: false,
                          },
                        }));
                      }}
                    >
                      + Add Item
                    </button>
                  )}
                </div>
              </div>
              {itemsLoading ? (
                <p className="vfp-items-hint">Loading line items…</p>
              ) : quoteLocked && lineItems.length === 0 ? (
                <p className="vfp-items-hint">This quote is {expandedQuote.status} — line items are locked.</p>
              ) : (
                <>
                  <LineItemsEditor
                    items={lineItems}
                    fields={lineItemFields(expandedQuote.categoryId)}
                    onChange={handleLineItemsChange}
                    showAddButton={false}
                    disabled={quoteLocked}
                    disabledMessage={`This quote is ${expandedQuote.status} — line items are locked.`}
                    total={{ compute: v => (Number(v.quantity) || 0) * (Number(v.unitCost) || 0) }}
                    // Shown in the summary block below instead, so the
                    // quote's total gets the same treatment as the
                    // procurement order's rather than a plain footer row.
                    showFooterTotal={false}
                  />
                  {lineItems.length > 0 && (
                    /* A quote's total is exactly the sum of its line items
                       (the server recomputes total_amount from them), so this
                       is the one figure — no subtotal/total pair, which would
                       just print the same number twice. */
                    <div className="lie-summary">
                      <div className="lie-summary-field">
                        <span className="lie-summary-label">Total</span>
                        <div className="lie-summary-box">
                          <span className="lie-summary-currency">{money.symbol}</span>
                          <span className="lie-summary-amount">
                            {lineItems
                              .reduce((sum, it) => sum + (Number(it.values.quantity) || 0) * (Number(it.values.unitCost) || 0), 0)
                              .toLocaleString(getAppLocale())}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          </div>

          <aside className="detail-sidebar">
            {/* The travel warning is context about the vendor, not a field you
                fill in, so it belongs beside the record rather than wedged
                between two form rows. */}
            {showTravelNotice && (
              <section className="side-section qt-travel-notice" role="note">
                <Icon name="warning" size={16} />
                <div>
                  <strong>
                    {quoteLocality === 'out-of-town'
                      ? 'Out-of-town vendor'
                      : 'Vendor location not recorded'}
                  </strong>
                  <p>{vendorLocalityReason(quoteLocality, quoteVendorLocation, projectCity)}</p>
                </div>
              </section>
            )}
            <section className="side-section">
              <h4>Quote Details</h4>
              <div className="bvfp-quote-detail">
                <div className="bvfp-quote-row"><span>Vendor</span><strong>{expandedQuote.vendorName}</strong></div>
                <div className="bvfp-quote-row"><span>Category</span><strong>{categoryName(expandedQuote.categoryId)}</strong></div>
                <div className="bvfp-quote-row"><span>Status</span>
                  <span className={`ds-badge ${expandedQuote.status === 'accepted' ? 'ds-badge-success' : expandedQuote.status === 'rejected' ? 'ds-badge-danger' : 'ds-badge-neutral'}`}>
                    {expandedQuote.status.charAt(0).toUpperCase() + expandedQuote.status.slice(1)}
                  </span>
                </div>
                <div className="bvfp-quote-row"><span>Date Received</span><strong>{expandedQuote.dateReceived ? formatDate(expandedQuote.dateReceived) : '—'}</strong></div>
                {/* Contingency sits to the left of Total, same row — mirrors
                    ProcurementOrderEdit's Contingency %/Subtotal layout.
                    Explicit draw against the Contingency budget line,
                    posted automatically once the quote is accepted
                    (contingency_draws server-side) — never a guessed
                    overage, only what's typed in here. */}
                <div className="bvfp-quote-row qt-total-contingency-row">
                  <div className="form-group qt-contingency-field">
                    <label>Contingency ({money.unit})</label>
                    <input
                      type="number"
                      min="0"
                      step="1000"
                      value={draft.contingencyAmount}
                      onChange={e => setDraft(d => ({ ...d, contingencyAmount: e.target.value }))}
                      placeholder="0"
                      disabled={quoteLocked}
                    />
                  </div>
                  <div className="qt-total-inline"><span>Total</span><strong>{money.format(expandedQuote.totalAmount)}</strong></div>
                </div>
              </div>
              {/* Moved out of the breadcrumb header (Header.tsx) — these are
                  a one-shot, irreversible decision on the whole quote, not a
                  field edit, so they live beside the rest of the quote's
                  details rather than next to the new Save button. Calls
                  handleAccept/handleReject directly now that both live in
                  this component — the quote:accept/quote:reject event
                  bridge only existed to cross from Header into here. */}
              {expandedQuote.status === 'pending' && (
                <div className="decision-actions">
                  <button
                    type="button"
                    className="decision-btn decision-btn--accept"
                    onClick={() => void handleAccept(expandedQuote).then(() => navigate('/quotes'))}
                  >
                    <Icon name="check" size={16} /> Accept
                  </button>
                  <button
                    type="button"
                    className="decision-btn decision-btn--reject"
                    onClick={async () => {
                      if (!(await confirm({
                        title: `Reject ${expandedQuote.vendorName}'s quote?`,
                        description: "This decision is recorded on the quote and can't be undone here.",
                        confirmLabel: 'Reject quote',
                      }))) return;
                      void handleReject(expandedQuote).then(() => navigate('/quotes'));
                    }}
                  >
                    <Icon name="x" size={16} /> Reject
                  </button>
                </div>
              )}
            </section>

            <AttachmentsPanel
              entityType="vendor_quote"
              entityId={quoteId}
              uploadLabel="+ Attach invoice or quote file"
              onError={showError}
              // An accepted or rejected quote is the frozen record of what was
              // bid — its paperwork shouldn't change either. Existing files
              // stay listed and openable.
              readOnly={quoteLocked}
              readOnlyMessage={expandedQuote ? `This quote is ${expandedQuote.status} — attachments are locked.` : undefined}
            />
          </aside>
          </div>

          {/* A quote is a competing bid that gets accepted or rejected, and
              the reasoning behind that decision only ever lived in someone's
              head. No history block: a quote's accept/reject decision is
              already frozen onto the record and shown in the sidebar. */}
          <RecordActivity entityType="vendor_quote" entityId={quoteId} />
        </>
      )}
    </div>
  );
};

export default QuotesTracker;
